import React, { useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'

const DAYS = [
  { key: 'mon', label: 'MON' },
  { key: 'tue', label: 'TUE' },
  { key: 'wed', label: 'WED' },
  { key: 'thu', label: 'THU' },
  { key: 'fri', label: 'FRI' },
  { key: 'sat', label: 'SAT' },
  { key: 'sun', label: 'SUN' }
]

const MAX_VIEWS_OPTIONS = [1, 2, 3, 5, 10]

function buildTimeSlots() {
  const slots = []

  for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
    const hours24 = Math.floor(minutes / 60)
    const mins = minutes % 60
    const period = hours24 < 12 ? 'AM' : 'PM'
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
    slots.push(`${String(hours12).padStart(2, '0')}:${String(mins).padStart(2, '0')}${period}`)
  }

  return slots
}

function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-sm font-medium text-dark-blue">{label}</label>
        {hint && <span title={hint}><UIcons.UilInfoCircle size={14} className="text-secondary" /></span>}
      </div>
      {children}
    </div>
  )
}

export default withTranslation()(function BudgetingStep({ t, form, updateField }) {
  const timeSlots = useMemo(() => buildTimeSlots(), [])
  const { data: frequenciesData } = useSWR('promos/frequencies')
  const { data: schedulesData } = useSWR('promos/schedules')

  const frequencies = frequenciesData?.data ?? []
  const schedules = schedulesData?.data ?? []

  const toggleDay = (key) => {
    updateField('daysToDisplay', form.daysToDisplay.includes(key)
      ? form.daysToDisplay.filter((day) => day !== key)
      : [...form.daysToDisplay, key])
  }

  const selectFrequency = (event) => {
    const id = event.target.value
    updateField('frequencyId', id)
    updateField('frequency', frequencies.find((option) => String(option.id) === id)?.name ?? '')
  }

  const toggleSchedule = (id) => {
    updateField('scheduleIds', form.scheduleIds.includes(id)
      ? form.scheduleIds.filter((scheduleId) => scheduleId !== id)
      : [...form.scheduleIds, id])
  }

  return (
    <div className="flex flex-col gap-6 max-w-[640px]">
      <h2 className="text-xl font-semibold text-dark-blue">{t('rewardAds.createFlow.budgeting.title')}</h2>

      <Field label={t('rewardAds.createFlow.budgeting.daily_budget.label')} hint={t('rewardAds.createFlow.budgeting.daily_budget.hint')}>
        <input
          type="number"
          min="0"
          value={form.dailyBudget}
          onChange={(event) => updateField('dailyBudget', event.target.value)}
          placeholder={t('rewardAds.createFlow.budgeting.daily_budget.placeholder')}
          className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label={t('rewardAds.createFlow.budgeting.start_date')}>
          <input
            type="date"
            value={form.startDate}
            onChange={(event) => updateField('startDate', event.target.value)}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          />
        </Field>

        <Field label={t('rewardAds.createFlow.budgeting.end_date')}>
          <input
            type="date"
            value={form.endDate}
            min={form.startDate || undefined}
            onChange={(event) => updateField('endDate', event.target.value)}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <Field label={t('rewardAds.createFlow.budgeting.days_to_display')} hint={t('rewardAds.createFlow.budgeting.days_to_display_hint')} />

        <div className="flex flex-wrap gap-2">
          {
            DAYS.map(({ key, label }) => {
              const active = form.daysToDisplay.includes(key)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleDay(key)}
                  className={`flex items-center gap-1.5 rounded-lg px-3 h-10 text-sm font-medium border ${
                    active ? 'bg-primary border-primary text-white' : 'border-border-lines text-dark-blue'
                  }`}
                >
                  {active && <UIcons.UilCheck size={14} />} {label}
                </button>
              )
            })
          }
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-dark-blue">{t('rewardAds.createFlow.budgeting.hours_to_display')}</label>
        <div className="flex items-center gap-3">
          <select
            value={form.startTime}
            onChange={(event) => updateField('startTime', event.target.value)}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          >
            <option value="" disabled>--</option>
            {timeSlots.map((slot) => <option key={slot} value={slot}>{slot}</option>)}
          </select>

          <span className="text-secondary shrink-0">-</span>

          <select
            value={form.endTime}
            onChange={(event) => updateField('endTime', event.target.value)}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          >
            <option value="" disabled>--</option>
            {timeSlots.map((slot) => <option key={slot} value={slot}>{slot}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label={t('rewardAds.createFlow.budgeting.frequency.label')}>
          <select
            value={form.frequencyId}
            onChange={selectFrequency}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          >
            <option value="" disabled>{t('rewardAds.createFlow.budgeting.frequency.placeholder')}</option>
            {frequencies.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
        </Field>

        <Field label={t('rewardAds.createFlow.budgeting.max_views.label')}>
          <select
            value={form.maxViewsPerUserDay}
            onChange={(event) => updateField('maxViewsPerUserDay', event.target.value)}
            className="w-full border border-border-lines rounded-lg px-3 py-2.5 text-sm text-dark-blue outline-none"
          >
            <option value="" disabled>{t('rewardAds.createFlow.budgeting.max_views.placeholder')}</option>
            {MAX_VIEWS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <Field label={t('rewardAds.createFlow.budgeting.schedules.label')} hint={t('rewardAds.createFlow.budgeting.schedules.hint')} />

        {!schedulesData && <p className="text-sm text-secondary">{t('rewardAds.createFlow.budgeting.schedules.loading')}</p>}
        {schedulesData && !schedules.length && <p className="text-sm text-secondary">{t('rewardAds.createFlow.budgeting.schedules.empty')}</p>}

        <div className="flex flex-wrap gap-2">
          {
            schedules.map((schedule) => {
              const active = form.scheduleIds.includes(schedule.id)
              return (
                <button
                  key={schedule.id}
                  type="button"
                  onClick={() => toggleSchedule(schedule.id)}
                  className={`flex items-center gap-1.5 rounded-lg px-3 h-10 text-sm font-medium border ${
                    active ? 'bg-primary border-primary text-white' : 'border-border-lines text-dark-blue'
                  }`}
                >
                  {active && <UIcons.UilCheck size={14} />} {schedule.day}: {schedule.from?.name_12} - {schedule.to?.name_12}
                </button>
              )
            })
          }
        </div>
      </div>
    </div>
  )
})
